import { createReducer } from "typesafe-actions";

import ChatFailureType from "@mapmycustomers/shared/enum/chat/ChatFailureType";
import ChatMessageRole from "@mapmycustomers/shared/enum/chat/ChatMessageRole";
import ChatProposalApplyOutcome from "@mapmycustomers/shared/enum/chat/ChatProposalApplyOutcome";
import ChatUserMessageStatus from "@mapmycustomers/shared/enum/chat/ChatUserMessageStatus";
import ChatMessage, {
  AssistantChatMessage,
  UserChatMessage,
} from "@mapmycustomers/shared/types/chat/ChatMessage";
import { FieldAgentApplyResultItem } from "@mapmycustomers/shared/types/chat/FieldAgentChat";

import {
  ChatProposalOutcomeDetail,
  ChatProposalStatusOverride,
  ChatProposalUiState,
} from "@app/util/chat/proposalRowStatus";

import {
  appendLocalChatMessage,
  applyChatMessageFeedback,
  applyProposals,
  cancelProposals,
  ChatConversationActions,
  clearChatConversation,
  discardProposalDraft,
  queueChatMessage,
  restoreChatConversation,
  retryChatMessage,
  saveProposalDraft,
  sendChatMessage,
  setChatMessageFeedback,
  toggleProposalChecked,
} from "./actions";
import type ChatConversationState from "./ChatConversationState";

export const chatConversationInitialState: ChatConversationState = {
  messages: [],
  proposalApply: {},
  proposalUi: {},
  suggestions: [],
};

const updateUserMessage = (
  messages: ChatMessage[],
  id: ChatMessage["id"],
  update: (message: UserChatMessage) => UserChatMessage
): ChatMessage[] =>
  messages.map((message) =>
    message.id === id && message.role === ChatMessageRole.USER ? update(message) : message
  );

const hasUserMessage = (messages: ChatMessage[], id: ChatMessage["id"]): boolean =>
  messages.some((message) => message.id === id && message.role === ChatMessageRole.USER);

const updateAssistantMessage = (
  messages: ChatMessage[],
  id: ChatMessage["id"],
  update: (message: AssistantChatMessage) => AssistantChatMessage
): ChatMessage[] =>
  messages.map((message) =>
    message.id === id && message.role === ChatMessageRole.ASSISTANT ? update(message) : message
  );

// The outcomes that settle a row for good. Membership only: the reducer stores the outcome itself,
// and what it looks like on screen is proposalRowStatus.ts's business.
const TERMINAL_OUTCOMES: ChatProposalStatusOverride[] = [
  ChatProposalApplyOutcome.APPLIED,
  ChatProposalApplyOutcome.CANCELLED,
  ChatProposalApplyOutcome.EXPIRED,
  ChatProposalApplyOutcome.FAILED,
  ChatProposalApplyOutcome.STALE,
];

// Shared by the CONFLICT and INVALID branches below: both attach the same three wire fields, and
// differ only in what else survives alongside them (statusOverride vs. spread previous).
const buildOutcomeDetail = (result: FieldAgentApplyResultItem): ChatProposalOutcomeDetail => ({
  failureReason: result.failureReason,
  fieldErrors: result.fieldErrors,
  freshValues: result.freshValues,
});

// The created record alone: a failure detail riding along would surface under an applied row's
// green check.
const buildCreatedEntityDetail = (
  result: FieldAgentApplyResultItem
): ChatProposalOutcomeDetail | undefined =>
  result.createdEntityId === undefined
    ? undefined
    : { createdEntityId: result.createdEntityId, createdEntityType: result.createdEntityType };

// Folds one apply result into the proposal-ui map. Terminal outcomes settle the row, keeping its
// draft and — on `applied` alone — the record it created (check state and any stale error stop
// mattering once settled);
// conflict settles the row too, but keeps its draft and freshValues so it can still render a
// before → after;
// invalid keeps the row pending and attaches the failure detail; not_found hides the row. The final
// fall-through is unreachable through the type, and kept for the deploy where BE ships a new outcome
// before we ship support for it: the row stays exactly as it was.
const foldOutcome = (
  proposalUi: Record<number, ChatProposalUiState>,
  result: FieldAgentApplyResultItem
): Record<number, ChatProposalUiState> => {
  const previous = proposalUi[result.proposalId];
  const terminal = TERMINAL_OUTCOMES.find((candidate) => candidate === result.outcome);
  if (terminal) {
    // The draft is the only record of what the rep applied — the result echoes no values back. The
    // created record is kept on `applied` alone, so a rejected row can never hold a link target.
    return {
      ...proposalUi,
      [result.proposalId]: {
        draft: previous?.draft,
        outcome:
          terminal === ChatProposalApplyOutcome.APPLIED
            ? buildCreatedEntityDetail(result)
            : undefined,
        statusOverride: terminal,
      },
    };
  }
  if (result.outcome === ChatProposalApplyOutcome.NOT_FOUND) {
    return { ...proposalUi, [result.proposalId]: { hidden: true } };
  }
  // Conflict settles the row — the proposal can never apply, because BE compares the Deal's live
  // stage against the frozen baseline in the stored proposal and nothing rewrites it. But unlike a
  // terminal outcome the row still renders: freshValues carries the new "before", the draft the
  // rep's "after". So it settles without being forgotten.
  if (result.outcome === ChatProposalApplyOutcome.CONFLICT) {
    return {
      ...proposalUi,
      [result.proposalId]: {
        draft: previous?.draft,
        outcome: buildOutcomeDetail(result),
        statusOverride: ChatProposalApplyOutcome.CONFLICT,
      },
    };
  }
  if (result.outcome === ChatProposalApplyOutcome.INVALID) {
    return {
      ...proposalUi,
      [result.proposalId]: { ...previous, outcome: buildOutcomeDetail(result) },
    };
  }
  return proposalUi;
};

const chatConversation = createReducer<ChatConversationState, ChatConversationActions>(
  chatConversationInitialState
)
  .handleAction(clearChatConversation, () => chatConversationInitialState)
  .handleAction(queueChatMessage, (state, { payload }) => ({
    ...state,
    messages: [
      ...state.messages,
      {
        id: payload.id,
        contextEntity: payload.contextEntity,
        createdAt: payload.createdAt,
        role: ChatMessageRole.USER,
        status: ChatUserMessageStatus.SENDING,
        text: payload.text,
        voice: payload.voice,
      },
    ],
    // The chips belonged to the turn that just ended.
    suggestions: [],
  }))
  .handleAction(restoreChatConversation, (state, { payload }) => ({
    ...state,
    conversationId: payload.conversationId,
    messages: payload.messages,
    // Never persisted: a reload always lands back on actionable, in-flight or not.
    proposalApply: {},
    proposalUi: payload.proposalUi ?? {},
    suggestions: [],
  }))
  .handleAction(retryChatMessage, (state, { payload }) => ({
    ...state,
    messages: updateUserMessage(state.messages, payload.id, (message) => ({
      ...message,
      failureType: undefined,
      status: ChatUserMessageStatus.SENDING,
    })),
  }))
  .handleAction(sendChatMessage.success, (state, { payload }) => {
    // A reply whose originating message is gone arrived after clearChatConversation wiped the
    // thread (sign-out/user-switch with the request in flight). Appending it would splice the
    // previous user's reply — and resurrect their conversationId — into the new thread, which
    // the persist watcher would then store under the new user's id. Drop it instead.
    if (!hasUserMessage(state.messages, payload.id)) {
      return state;
    }
    return {
      ...state,
      conversationId: payload.conversationId,
      messages: [
        ...updateUserMessage(state.messages, payload.id, (message) => ({
          ...message,
          status: ChatUserMessageStatus.SENT,
        })),
        // Always at the end, even when the message being answered is not the last one — so a
        // retried message's reply reads as an answer to whatever the rep asked after it.
        // Accepted while TEXT is the only content type: inserting mid-thread would mean the
        // persisted order no longer matches the order the rep saw, for a case (retrying an old
        // message rather than the newest one) that is rare and already flagged by its own icon.
        ...payload.messages,
      ],
      suggestions: payload.suggestions,
    };
  })
  .handleAction(sendChatMessage.failure, (state, { payload }) => {
    // The failing turn belongs to a thread clearChatConversation already wiped (sign-out/
    // user-switch with the request in flight). Dropping the conversation id now would discard
    // the one the new conversation has since earned, restarting it server-side.
    if (!hasUserMessage(state.messages, payload.id)) {
      return state;
    }
    return {
      ...state,
      // A 404 means the server has no such conversation, so the next attempt must start a new
      // one rather than quoting an id that will fail again.
      conversationId:
        payload.failureType === ChatFailureType.CONVERSATION_GONE
          ? undefined
          : state.conversationId,
      messages: updateUserMessage(state.messages, payload.id, (message) => ({
        ...message,
        failureType: payload.failureType,
        status: ChatUserMessageStatus.FAILED,
      })),
    };
  })
  // Not setChatMessageFeedback.request: see the comment on that action in actions.ts for why
  // the optimistic write has to be its own action rather than living on the request.
  .handleAction(applyChatMessageFeedback, (state, { payload }) => ({
    ...state,
    messages: updateAssistantMessage(state.messages, payload.id, (message) => ({
      ...message,
      feedback: payload.feedback,
    })),
  }))
  .handleAction(setChatMessageFeedback.failure, (state, { payload }) => ({
    ...state,
    messages: updateAssistantMessage(state.messages, payload.id, (message) => ({
      ...message,
      feedback: payload.feedback,
    })),
  }))
  .handleAction(appendLocalChatMessage, (state, { payload }) => ({
    ...state,
    messages: [...state.messages, { ...payload, role: ChatMessageRole.LOCAL }],
  }))
  .handleAction(toggleProposalChecked, (state, { payload }) => ({
    ...state,
    proposalUi: {
      ...state.proposalUi,
      [payload.proposalId]: { ...state.proposalUi[payload.proposalId], checked: payload.checked },
    },
  }))
  .handleAction(saveProposalDraft, (state, { payload }) => ({
    ...state,
    proposalUi: {
      ...state.proposalUi,
      // A re-edit clears the stale error: the rep is trying again, so the old outcome no
      // longer describes what's about to be submitted.
      [payload.proposalId]: {
        ...state.proposalUi[payload.proposalId],
        draft: payload.draft,
        outcome: undefined,
      },
    },
  }))
  .handleAction(discardProposalDraft, (state, { payload }) => ({
    ...state,
    proposalUi: {
      ...state.proposalUi,
      [payload.proposalId]: { ...state.proposalUi[payload.proposalId], draft: undefined },
    },
  }))
  // Both gestures share the card's single busy slot. The cancel saga must NOT dispatch
  // applyProposals.request—sagas.ts takes every one and would run the Confirm handler too.
  .handleAction([applyProposals.request, cancelProposals], (state, { payload }) => ({
    ...state,
    proposalApply: { ...state.proposalApply, [payload.messageId]: "inFlight" },
  }))
  .handleAction(applyProposals.failure, (state, { payload }) => ({
    ...state,
    proposalApply: { ...state.proposalApply, [payload.messageId]: "failed" },
  }))
  .handleAction(applyProposals.success, (state, { payload }) => {
    const proposalApply = { ...state.proposalApply };
    delete proposalApply[payload.messageId];
    return {
      ...state,
      proposalApply,
      proposalUi: payload.results.reduce(foldOutcome, state.proposalUi),
    };
  });

export default chatConversation;
